/**
 * Breath Motion — silhouette の idle 呼吸アニメ（happa v1.1 Phase D / Phase H Step 1）。
 *
 * **単層構成**（Phase H Step 1 で簡素化）:
 *
 * - **Pulse**: 重心基準の放射スケール。ゆっくりとした吸・吐（sin 波）。
 *
 * 合成式:
 * ```
 * displaced_i = vertex_i + pulseDisp_i
 * pulseDisp_i = (vertex_i - centroid) × (sin(2π × t / pulsePeriod) × pulseAmp/100 × 0.05)
 * ```
 *
 * 履歴:
 * - Phase F Step 2: Flicker レイヤー（高周波 simplex noise）を削除
 * - Phase F Step 3: Rotation レイヤー（重心中心の等速連続回転）を追加
 * - Phase H Step 1: Crackle（短パルスの noise ざわめき）と Rotation を削除
 *   して Pulse 単層に整理。simplex-noise への依存も外した。
 *
 * pure 性:
 * - 副作用なし（DOM / Canvas / performance.now 非参照）。
 * - 同 `t` + 同 `params` + 同 `silhouette` → 同出力（`seed` も不要）。
 *
 * 設計参照:
 * - `docs/happa-designtool.md` Phase D 仕様（Breath Motion）
 * - `AppState.breath`（state/types.ts）= `BreathParams` の各パラメータ定義
 */

import type { BreathParams } from '../state/types';
import { polygonCentroid } from './polygon';
import type { Vec2 } from './types';

/**
 * Pulse レイヤー: 重心基準の放射スケール（sin 波で周期的に拡縮）。
 *
 * 式:
 * ```
 * pulseScale(t) = 1 + sin(2π × t / pulsePeriod) × (pulseAmp/100 × 0.05)
 * pulseDisp_i   = (vertex_i - centroid) × (pulseScale(t) - 1)
 * ```
 *
 * 最大振幅は amp=100 のとき scale 1±0.05（silhouette が 5% 伸縮）。
 * amp=0 なら呼び出し側で skip される想定（本関数は呼ばれない）。
 */
function computePulseDisps(
  silhouette: Vec2[],
  centroid: Vec2,
  t: number,
  amp: number,
  period: number,
): Vec2[] {
  const factor = Math.sin((2 * Math.PI * t) / period) * (amp / 100) * 0.05;
  return silhouette.map(
    (v): Vec2 => [(v[0] - centroid[0]) * factor, (v[1] - centroid[1]) * factor],
  );
}

/**
 * silhouette に Breath Motion（Pulse のみ）を適用する。
 *
 * 処理:
 * - `pulseAmp === 0` なら計算を skip して入力のシャローコピーを返す
 *   （60fps × N 頂点のホットパス最適化）。
 * - それ以外は重心基準の放射スケールを加算した新しい頂点列を返す。
 *
 * 位相凍結: `t` が凍結されれば Pulse も凍結される（`t` ドライブ）。
 * DETONATE 中は Clock.pause() により呼吸が止まる。
 *
 * @param silhouette 元の silhouette 頂点列（warp 後、Breath 適用前）。
 * @param t 経過秒（Clock.t 由来）。pause 中は凍結値が渡る想定。
 * @param params `AppState.breath`（pulseAmp / pulsePeriod）。
 * @returns Pulse 適用後の新しい頂点列。元の silhouette は不変。
 */
export function applyBreath(silhouette: Vec2[], t: number, params: BreathParams): Vec2[] {
  if (silhouette.length === 0) return [];
  if (params.pulseAmp <= 0) return silhouette.slice();

  const centroid = polygonCentroid(silhouette);
  const pulseDisps = computePulseDisps(
    silhouette,
    centroid,
    t,
    params.pulseAmp,
    params.pulsePeriod,
  );
  return silhouette.map((v, i): Vec2 => {
    const p = pulseDisps[i];
    if (!p) return [v[0], v[1]];
    return [v[0] + p[0], v[1] + p[1]];
  });
}
